import { createAction, createReducer } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import LoadingStatus from '@libs/loading-status';

import {
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_FAILURE,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_SUCCESS,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_FAILURE,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_SUCCESS,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_BUSINESS_CATEGORIES_FETCH_FAILURE,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_BUSINESS_CATEGORIES_FETCH_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_BUSINESS_CATEGORIES_FETCH_SUCCESS,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_FAILURE,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_SUCCESS,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_FETCH_FAILURE,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_FETCH_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_FETCH_SUCCESS,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_REGISTER_FAILURE,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_REGISTER_REQUEST,
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_REGISTER_SUCCESS,
} from '@app/store/actionTypes';
import { RootState } from '@app/store/types';

import BusinessCategory from './types/BusinessCategory';
import KakaoTalkChannel from './types/KakaoTalkChannel';

function formatAuthenticationErrorMessage(
  msg: string,
  businessCategories: BusinessCategory[] | null
): string {
  // 토스트 서버에서 아래와 같이 한글로 오류메시지가 옵니다.
  const regex =
    /^기존에 등록된 프로필의 카테고리 코드와 일치하지 않습니다\. 동일하게 입력해주세요\. 카테고리 코드 : (\d{11})/;
  const regexResult = regex.exec(msg);

  if (regexResult) {
    const categoryCode = regexResult[1]; // e.g. 00100020003

    const firstCode = categoryCode.slice(0, 3);
    const secondCode = categoryCode.slice(3, 7);
    const thirdCode = categoryCode.slice(7, 11);

    const firstCategory = businessCategories?.find((t) => t.code === firstCode);
    const secondCategory = firstCategory?.subCategories?.find((t) => t.code === secondCode);
    const thirdCategory = secondCategory?.subCategories?.find((t) => t.code === thirdCode);

    const categoryHierarchy = `${firstCategory?.name ?? firstCode} > ${
      secondCategory?.name ?? secondCode
    } > ${thirdCategory?.name ?? thirdCode}`;

    return `기존에 등록된 프로필의 카테고리와 일치하지 않습니다. "${categoryHierarchy}" 카테고리로 설정해주세요.`;
  }

  return msg;
}

export const activateKakaoTalkChannel = createAction<{
  plusFriendId: string;
}>(CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_REQUEST);

export const activateKakaoTalkChannelSuccess = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_SUCCESS
);

export const activateKakaoTalkChannelFailure = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_ACTIVATE_FAILURE,
  (payload: AxiosError) => {
    return { payload, error: true };
  }
);

export const authenticateKakaoTalkChannel = createAction<{
  authenticationCode: number;
  categoryCode: string;
  plusFriendId: string;
}>(CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_REQUEST);

export const authenticateKakaoTalkChannelSuccess = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_SUCCESS
);

export const authenticateKakaoTalkChannelFailure = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_AUTHENTICATE_FAILURE,
  (payload: Error) => {
    return { payload, error: true };
  }
);

export const deactivateKakaoTalkChannel = createAction<{
  plusFriendId: string;
}>(CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_REQUEST);

export const deactivateKakaoTalkChannelSuccess = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_SUCCESS
);

export const deactivateKakaoTalkChannelFailure = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_DEACTIVATE_FAILURE,
  (payload: AxiosError) => {
    return { payload, error: true };
  }
);

export const fetchKakaoTalkBusinessCategories = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_BUSINESS_CATEGORIES_FETCH_REQUEST
);

export const fetchKakaoTalkBusinessCategoriesSuccess = createAction<BusinessCategory[] | null>(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_BUSINESS_CATEGORIES_FETCH_SUCCESS
);

export const fetchKakaoTalkBusinessCategoriesFailure = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_BUSINESS_CATEGORIES_FETCH_FAILURE,
  (payload: AxiosError) => {
    return { payload, error: true };
  }
);

export const fetchKakaoTalkChannel = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_FETCH_REQUEST
);

export const fetchKakaoTalkChannelSuccess = createAction<KakaoTalkChannel | null>(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_FETCH_SUCCESS
);

export const fetchKakaoTalkChannelFailure = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_FETCH_FAILURE,
  (payload: AxiosError) => {
    return { payload, error: true };
  }
);

export const registerKakaoTalkChannel = createAction<{
  categoryCode: string;
  phoneNumber: string;
  plusFriendId: string;
}>(CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_REGISTER_REQUEST);

export const registerKakaoTalkChannelSuccess = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_REGISTER_SUCCESS
);

export const registerKakaoTalkChannelFailure = createAction(
  CUSTOM_BRAND_SETTING__KAKAOTALK_CHANNEL_REGISTER_FAILURE,
  (payload: Error) => {
    return { payload, error: true };
  }
);

export type CustomBrandSettingKakaoTalkAction =
  | ReturnType<typeof activateKakaoTalkChannel>
  | ReturnType<typeof activateKakaoTalkChannelSuccess>
  | ReturnType<typeof activateKakaoTalkChannelFailure>
  | ReturnType<typeof authenticateKakaoTalkChannel>
  | ReturnType<typeof authenticateKakaoTalkChannelSuccess>
  | ReturnType<typeof authenticateKakaoTalkChannelFailure>
  | ReturnType<typeof deactivateKakaoTalkChannel>
  | ReturnType<typeof deactivateKakaoTalkChannelSuccess>
  | ReturnType<typeof deactivateKakaoTalkChannelFailure>
  | ReturnType<typeof fetchKakaoTalkBusinessCategories>
  | ReturnType<typeof fetchKakaoTalkBusinessCategoriesSuccess>
  | ReturnType<typeof fetchKakaoTalkBusinessCategoriesFailure>
  | ReturnType<typeof fetchKakaoTalkChannel>
  | ReturnType<typeof fetchKakaoTalkChannelSuccess>
  | ReturnType<typeof fetchKakaoTalkChannelFailure>
  | ReturnType<typeof registerKakaoTalkChannel>
  | ReturnType<typeof registerKakaoTalkChannelSuccess>
  | ReturnType<typeof registerKakaoTalkChannelFailure>;

type kakaoTalkState = {
  businessCategories: BusinessCategory[] | null;
  businessCategoriesLoadingStatus: LoadingStatus;
  channelActivationLoadingStatus: LoadingStatus;
  channelAuthenticationLoadingStatus: LoadingStatus;
  channelDeactivationLoadingStatus: LoadingStatus;
  channelLoadingStatus: LoadingStatus;
  channelRegistrationLoadingStatus: LoadingStatus;
  errorMessage: string | null;
  id: string | null;
  isActivated: boolean;
  plusFriendId: string | null;
};

const initialState: kakaoTalkState = {
  businessCategories: null,
  businessCategoriesLoadingStatus: LoadingStatus.IDLE,
  channelActivationLoadingStatus: LoadingStatus.IDLE,
  channelAuthenticationLoadingStatus: LoadingStatus.IDLE,
  channelDeactivationLoadingStatus: LoadingStatus.IDLE,
  channelLoadingStatus: LoadingStatus.IDLE,
  channelRegistrationLoadingStatus: LoadingStatus.IDLE,
  errorMessage: null,
  id: null,
  isActivated: false,
  plusFriendId: null,
};

export const reducer = createReducer<kakaoTalkState>(initialState, (builder) => {
  builder
    .addCase(activateKakaoTalkChannel, (state) => {
      state.channelActivationLoadingStatus = LoadingStatus.PENDING;
      state.errorMessage = null;
    })
    .addCase(activateKakaoTalkChannelSuccess, (state) => {
      state.channelActivationLoadingStatus = LoadingStatus.SUCCEEDED;
      state.isActivated = true;
    })
    .addCase(activateKakaoTalkChannelFailure, (state, action) => {
      state.channelActivationLoadingStatus = LoadingStatus.FAILED;
      state.errorMessage = action.payload.message;
    })
    .addCase(authenticateKakaoTalkChannel, (state) => {
      state.channelAuthenticationLoadingStatus = LoadingStatus.PENDING;
      state.errorMessage = null;
    })
    .addCase(authenticateKakaoTalkChannelSuccess, (state) => {
      state.channelAuthenticationLoadingStatus = LoadingStatus.SUCCEEDED;
    })
    .addCase(authenticateKakaoTalkChannelFailure, (state, action) => {
      state.channelAuthenticationLoadingStatus = LoadingStatus.FAILED;
      state.errorMessage = formatAuthenticationErrorMessage(
        action.payload.message,
        state.businessCategories
      );
    })
    .addCase(deactivateKakaoTalkChannel, (state) => {
      state.businessCategoriesLoadingStatus = LoadingStatus.IDLE;
      state.channelActivationLoadingStatus = LoadingStatus.IDLE;
      state.channelAuthenticationLoadingStatus = LoadingStatus.IDLE;
      state.channelDeactivationLoadingStatus = LoadingStatus.PENDING;
      state.channelRegistrationLoadingStatus = LoadingStatus.IDLE;
      state.errorMessage = null;
    })
    .addCase(deactivateKakaoTalkChannelSuccess, (state) => {
      state.channelDeactivationLoadingStatus = LoadingStatus.SUCCEEDED;
      state.isActivated = false;
    })
    .addCase(deactivateKakaoTalkChannelFailure, (state, action) => {
      state.channelDeactivationLoadingStatus = LoadingStatus.FAILED;
      state.errorMessage = action.payload.message;
    })
    .addCase(fetchKakaoTalkBusinessCategories, (state) => {
      state.businessCategories = null;
      state.businessCategoriesLoadingStatus = LoadingStatus.PENDING;
      state.channelActivationLoadingStatus = LoadingStatus.IDLE;
      state.channelAuthenticationLoadingStatus = LoadingStatus.IDLE;
      state.channelDeactivationLoadingStatus = LoadingStatus.IDLE;
      state.channelRegistrationLoadingStatus = LoadingStatus.IDLE;
      state.errorMessage = null;
    })
    .addCase(fetchKakaoTalkBusinessCategoriesSuccess, (state, action) => {
      const businessCategories = action.payload;

      state.businessCategories = businessCategories;
      state.businessCategoriesLoadingStatus = LoadingStatus.SUCCEEDED;
    })
    .addCase(fetchKakaoTalkBusinessCategoriesFailure, (state, action) => {
      state.businessCategoriesLoadingStatus = LoadingStatus.FAILED;
      state.errorMessage = action.payload.message;
    })
    .addCase(fetchKakaoTalkChannel, (state) => {
      state.businessCategories = null;
      state.businessCategoriesLoadingStatus = LoadingStatus.IDLE;
      state.channelActivationLoadingStatus = LoadingStatus.IDLE;
      state.channelAuthenticationLoadingStatus = LoadingStatus.IDLE;
      state.channelDeactivationLoadingStatus = LoadingStatus.IDLE;
      state.channelLoadingStatus = LoadingStatus.PENDING;
      state.channelRegistrationLoadingStatus = LoadingStatus.IDLE;
      state.errorMessage = null;
      state.id = null;
      state.isActivated = false;
      state.plusFriendId = null;
    })
    .addCase(fetchKakaoTalkChannelSuccess, (state, action) => {
      const kakaoTalkChannel = action.payload;

      state.channelLoadingStatus = LoadingStatus.SUCCEEDED;
      state.id = kakaoTalkChannel?.id ?? null;
      state.isActivated = kakaoTalkChannel?.isActivated ?? false;
      state.plusFriendId = kakaoTalkChannel?.plusFriendId ?? null;
    })
    .addCase(fetchKakaoTalkChannelFailure, (state, action) => {
      state.channelLoadingStatus = LoadingStatus.FAILED;
      state.errorMessage = action.payload.message;
    })
    .addCase(registerKakaoTalkChannel, (state) => {
      state.channelRegistrationLoadingStatus = LoadingStatus.PENDING;
      state.errorMessage = null;
    })
    .addCase(registerKakaoTalkChannelSuccess, (state) => {
      state.channelRegistrationLoadingStatus = LoadingStatus.SUCCEEDED;
    })
    .addCase(registerKakaoTalkChannelFailure, (state, action) => {
      state.channelRegistrationLoadingStatus = LoadingStatus.FAILED;
      state.errorMessage = action.payload.message;
    });
});

const getKakaoTalk = (state: RootState) => state.customBrandSetting.kakaoTalk;

export const getBusinessCategories = (state: RootState) => getKakaoTalk(state).businessCategories;
export const getBusinessCategoriesLoadingStatus = (state: RootState) =>
  getKakaoTalk(state).businessCategoriesLoadingStatus;
export const getChannelActivationLoadingStatus = (state: RootState) =>
  getKakaoTalk(state).channelActivationLoadingStatus;
export const getChannelAuthenticationLoadingStatus = (state: RootState) =>
  getKakaoTalk(state).channelAuthenticationLoadingStatus;
export const getChannelDeactivationLoadingStatus = (state: RootState) =>
  getKakaoTalk(state).channelDeactivationLoadingStatus;
export const getChannelLoadingStatus = (state: RootState) =>
  getKakaoTalk(state).channelLoadingStatus;
export const getChannelRegistrationLoadingStatus = (state: RootState) =>
  getKakaoTalk(state).channelRegistrationLoadingStatus;
export const getErrorMessage = (state: RootState) => getKakaoTalk(state).errorMessage;
export const getPlusFriendId = (state: RootState) => getKakaoTalk(state).plusFriendId;
